import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import debounce from 'lodash.debounce';
import { useCallback, useMemo, useState } from 'react';
import { User } from 'lucide-react';
import {
	Badge,
	ChipList,
	DataGrid,
	FilterAsyncAutoComplete,
	FilterAutoCompleteById,
	FilterDateRange,
	FilterSearch,
	RequirementChip,
	Spinner,
} from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import { Project } from '@repo/shared/domain';
import { EventForm } from '@repo/forms';
import { EventEmitter, reportDownload, constructQuery } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useLocalStorage } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getEvents } from 'src/api/admin/events';
import { getEventTypes } from 'src/api/admin/types';
import { getZones } from 'src/api/admin/sites';
import { getProjects } from 'src/api/admin/projects';
import { getAllTags } from 'src/api/admin/tags';
import { getReport } from 'src/helpers/getReport';

interface EventsSearch {
	sort?: string;
	page?: number;
	search?: string;
	groupIds?: string;
	zoneIds?: string;
	typeIds?: string;
	userIds?: string;
	projectIds?: string;
	from?: string;
	until?: string;
	onlyTopLevel?: string;
	tab?: string;
	timezone?: string;
}

export const Route = createFileRoute('/_admin/events/')({
	validateSearch: (search: Record<string, unknown>): EventsSearch => ({
		sort: search.sort as string | undefined,
		page: search.page !== undefined ? Number(search.page) : undefined,
		search: search.search as string | undefined,
		groupIds: search.groupIds as string | undefined,
		zoneIds: search.zoneIds as string | undefined,
		typeIds: search.typeIds as string | undefined,
		userIds: search.userIds as string | undefined,
		projectIds: search.projectIds as string | undefined,
		from: search.from as string | undefined,
		until: search.until as string | undefined,
		onlyTopLevel: search.onlyTopLevel as string | undefined,
		tab: search.tab as string | undefined,
		timezone: search.timezone as string | undefined,
	}),
	component: EventsPage,
});

const getDateRanges = () => {
	const today = new Date();
	const tomorrow = addDays(today, 1);

	const startOfToday = startOfDay(today).toISOString();
	const endOfToday = endOfDay(today).toISOString();

	const startOfTomorrow = startOfDay(tomorrow).toISOString();
	const endOfTomorrow = endOfDay(tomorrow).toISOString();

	return {
		startOfToday,
		endOfToday,
		startOfTomorrow,
		endOfTomorrow,
	};
};

const downloadReport = async (query: string) => {
	const blob = await getReport('events', query);
	if (!blob) {
		return;
	}
	reportDownload(blob);
};

const tabs = [
	{
		id: '0',
		label: 'All',
	},
	{
		id: '1',
		label: 'Start Today',
	},
	{
		id: '2',
		label: 'Start Tomorrow',
	},
	{
		id: 'archived',
		label: 'Archived',
	},
];

const getColumns = (usesSubEvents: boolean) => [
	{
		field: 'name',
		headerName: 'Name',
		flex: 1,
	},
	...(usesSubEvents
		? [
				{
					field: 'project',
					headerName: 'Project',
					flex: 1,
					renderCell: (c: { value: Project | null }) => {
						return c.value?.name ? <Badge>{c.value.name}</Badge> : null;
					},
					sortable: false,
				},
			]
		: []),
	{
		field: 'type',
		headerName: 'Type',
		flex: 1,
		renderCell: (c: { row: unknown }) => <RequirementChip object={c.row} />,
		sortable: false,
	},
	{
		field: 'startTime',
		headerName: 'Date From',
		width: 120,
		valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'finishTime',
		headerName: 'Date To',
		width: 120,
		valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'zones',
		headerName: 'Zones',
		flex: 1,
		renderCell: (c: { value: Array<{ name?: string }> }) => <ChipList data={c.value} getLabel={(o) => o?.name} />,
		sortable: false,
	},
	{
		field: 'users',
		headerName: 'Users',
		flex: 1,
		renderCell: (c: { value: Array<{ firstName?: string; lastName?: string; email?: string; status?: string }> }) => (
			<ChipList
				data={c.value.map((user) => ({ ...user, status: undefined }))}
				getLabel={(o) => (o.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email) || null}
				icon={<User size={14} className="text-gray-500" />}
			/>
		),
		sortable: false,
	},
	{
		field: 'groups',
		headerName: 'Companies',
		flex: 1,
		renderCell: (c: { value: Array<{ name?: string }> }) => <ChipList data={c.value} getLabel={(o) => o?.name} />,
		sortable: false,
	},
	{
		field: 'admins',
		headerName: 'Contacts',
		flex: 1,
		renderCell: (c: { value: Array<{ firstName?: string; lastName?: string; email?: string; status?: string }> }) => (
			<ChipList
				data={c.value.map((user) => ({ ...user, status: undefined }))}
				getLabel={(o) => (o.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email) || null}
				icon={<User size={14} className="text-gray-500" />}
			/>
		),
		sortable: false,
	},
	{
		field: 'updatedAt',
		headerName: 'Updated',
		width: 120,
		valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleDateString('en-GB'),
	},
];

function EventsPage() {
	const searchParams = Route.useSearch();
	const {
		sort,
		page,
		search,
		groupIds,
		zoneIds,
		typeIds,
		userIds,
		projectIds,
		from: searchFrom,
		until: searchUntil,
		onlyTopLevel,
		tab,
	} = searchParams;

	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();
	const { updateValue } = useLocalStorage<string>('eventsBackUrl');
	useBreadcrumbLevel(0, 'Events');

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config, loading: configLoading } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const usesSubEvents = featureFlags?.USES_SUBCONTRACTING || false;
	const usesTags = featureFlags?.USES_TAGS || false;

	const [isFormOpened, setIsFormOpened] = useState(false);

	const activeTab = parseInt(tab || '0', 10);

	// Calculate date ranges based on tab
	const { startOfToday, endOfToday, startOfTomorrow, endOfTomorrow } = getDateRanges();
	const { from, until } = {
		from: tab === '1' ? startOfToday : tab === '2' ? startOfTomorrow : searchFrom ? searchFrom : undefined,
		until: tab === '1' ? endOfToday : tab === '2' ? endOfTomorrow : searchUntil ? searchUntil : undefined,
	};

	const query = constructQuery({
		tab,
		sort: sort || 'startTime:desc',
		page,
		search,
		groupIds,
		zoneIds,
		typeIds,
		userIds,
		projectIds,
		from,
		until,
		// Default should be "All Events" (no parentEventId filtering).
		// Only include this param when explicitly set to 'true'.
		onlyTopLevel: onlyTopLevel === 'true' ? 'true' : undefined,
	});

	const { data: eventsData, isLoading: eventsLoading } = useQuery({
		queryKey: ['events', query],
		queryFn: () => getEvents(query),
	});

	const { data: typesData } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes(),
	});

	const { data: zonesData } = useQuery({
		queryKey: ['zones'],
		queryFn: () => getZones(),
	});

	const { data: projectsData } = useQuery({
		queryKey: ['projects'],
		queryFn: () => getProjects(),
		enabled: usesSubEvents,
	});

	const { data: tagsData } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
		enabled: usesTags,
	});

	const data = useMemo(() => eventsData || { data: [], count: 0, page: 0, pageSize: 25 }, [eventsData]);
	const types = typesData?.data || [];
	const zones = zonesData?.data || [];
	const tags = tagsData || [];

	const columns = useMemo(() => getColumns(usesSubEvents), [usesSubEvents]);

	const downloadCSVReport = useCallback(async () => {
		const params = new URLSearchParams();
		if (sort) params.set('sort', sort);
		if (page !== undefined) params.set('page', String(page));
		if (search) params.set('search', search);
		if (groupIds) params.set('groupIds', groupIds);
		if (zoneIds) params.set('zoneIds', zoneIds);
		if (typeIds) params.set('typeIds', typeIds);
		if (userIds) params.set('userIds', userIds);
		if (projectIds) params.set('projectIds', projectIds);
		if (from) params.set('from', from);
		if (until) params.set('until', until);
		if (onlyTopLevel) params.set('onlyTopLevel', onlyTopLevel);
		if (tab) params.set('tab', tab);
		downloadReport(params.toString());
	}, [sort, page, search, groupIds, zoneIds, typeIds, userIds, projectIds, from, until, onlyTopLevel, tab]);

	const defaultEventData = {
		startTime: startOfDay(new Date()),
		finishTime: endOfDay(new Date()),
		adminIds: authenticatedUser ? [authenticatedUser.id] : [],
	};

	const handleSort = (sortModel: { field: string; sort: 'asc' | 'desc' }[]) => {
		const firstSort = sortModel[0];
		if (!firstSort) {
			return;
		}
		updateQueryParams({ sort: sortToString(firstSort) });
	};

	const handleRowClick = ({ row }: { row: { id: string } }) => {
		updateValue(window.location.href);
		navigate({ to: '/events/$eventId', params: { eventId: row.id } });
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const handleFilterChange = (filterData: {
		projectIds?: string[] | string;
		groupIds?: string[] | string;
		zoneIds?: string[] | string;
		typeIds?: string[] | string;
		userIds?: string[] | string;
		search?: string;
		onlyTopLevel?: string;
	}) => {
		updateQueryParams(filterData);
	};

	const handlePaginationModelChange = ({ page: newPage }: { page: number }) => {
		updateQueryParams({ page: newPage });
	};

	const handleTabChange = (newTab: string) => {
		updateQueryParams({
			tab: newTab,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		});
	};

	const handleDateFilterChange = (dateFilter: { from: string; until: string }) => {
		updateQueryParams(dateFilter);
	};

	const revalidateEvents = () => {
		queryClient.invalidateQueries({ queryKey: ['events'] });
	};

	const handleCloseForm = () => {
		setIsFormOpened(false);
	};

	const handleAddEvent = async (formData: Record<string, unknown>) => {
		let isError = false;
		let id: string | undefined;

		if (formData) {
			await request
				.post('/api/events', formData)
				.then(({ body }: { body: { id: string } }) => {
					EventEmitter.emit('toast', {
						type: 'success',
						message: 'Event saved',
					});
					id = body.id;
				})
				.catch((error: { message?: string; body?: { message?: string } }) => {
					isError = true;
					const errorMessage =
						error?.message || error?.body?.message || 'Failed to save Event. Did you enter all data correctly?';
					EventEmitter.emit('toast', {
						type: 'error',
						message: errorMessage,
					});
				});
		}

		if (id) {
			updateValue(window.location.href);
			navigate({ to: '/events/$eventId', params: { eventId: id } });
		} else {
			revalidateEvents();
		}

		if (!isError) {
			setIsFormOpened(false);
		}
	};

	const handleOpenForm = useCallback(() => {
		setIsFormOpened(true);
	}, []);

	const actions = useMemo(() => {
		return [
			<Button onClick={handleOpenForm} startIcon={<AddIcon />} variant="outlined" key="add">
				Add Event
			</Button>,
		];
	}, [handleOpenForm]);

	const isLoading = authLoading || configLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<GenericDataPageHeader
				title="Event"
				tabs={tabs}
				defaultTab="0"
				onTabChange={handleTabChange}
				actions={actions}
				downloadCSVReport={downloadCSVReport}
			/>
			<div className="p-4 pt-0">
				{/* Filter */}
				<div className="mt-2 flex flex-row flex-wrap gap-2">
					<div className="w-3/5 flex-grow flex gap-2">
						<FilterSearch
							label="Search Event"
							defaultValue={search || ''}
							onChange={debounce((searchInput: string) => {
								handleSearchChange(searchInput);
							}, 350)}
							sx={{ width: '100%', minWidth: '40%', flexGrow: 1 }}
						/>
						<FilterAsyncAutoComplete
							key="userFilter"
							label="Filter Users"
							value={userIds || []}
							onChange={(newUserIds) => handleFilterChange({ userIds: newUserIds })}
							dataSource="/api/data-sources/users/data"
							optionLabel="name"
							name="userIds"
							multiple
						/>
						<FormControl sx={{ minWidth: 150 }}>
							<Select
								labelId="event-level-filter-label"
								value={onlyTopLevel === 'true' ? 'topLevel' : 'all'}
								onChange={(e) => {
									const nextOnlyTopLevel = e.target.value === 'topLevel' ? 'true' : undefined;
									handleFilterChange({ onlyTopLevel: nextOnlyTopLevel });
								}}
							>
								<MenuItem value="topLevel">Main Events</MenuItem>
								<MenuItem value="all">All Events</MenuItem>
							</Select>
						</FormControl>
					</div>
					<div className="w-2/5 flex-grow flex gap-2">
						{usesSubEvents && (
							<FilterAsyncAutoComplete
								key="projectFilter"
								name="projectIds"
								label="Filter Projects"
								filter="projectsArchived"
								dataSource="/api/data-sources/projects/data"
								value={projectIds || []}
								onChange={(newProjectIds) => handleFilterChange({ projectIds: newProjectIds })}
								multiple
							/>
						)}
						<FilterAsyncAutoComplete
							key="typeFilter"
							name="typeIds"
							label="Filter Types"
							dataSource="/api/data-sources/event-types/data"
							value={typeIds || []}
							onChange={(newTypeIds) => handleFilterChange({ typeIds: newTypeIds })}
							multiple
						/>
						<FilterAutoCompleteById
							key="zoneFilter"
							label="Filter Zones"
							options={zones}
							value={zoneIds || []}
							onChange={(newZoneIds) => handleFilterChange({ zoneIds: newZoneIds })}
							multiple
						/>
						<FilterAsyncAutoComplete
							key="groupFilter"
							name="groupIds"
							label="Filter Companies"
							optionLabel="name"
							dataSource="/api/data-sources/groups/data"
							value={groupIds || []}
							onChange={(newGroupIds) => handleFilterChange({ groupIds: newGroupIds })}
							multiple
						/>
						{activeTab === 0 && (
							<FilterDateRange
								label="Filter Date Range"
								value={
									searchFrom || searchUntil
										? {
												from: searchFrom,
												until: searchUntil,
											}
										: undefined
								}
								onChange={handleDateFilterChange}
								sx={{ width: 330 }}
							/>
						)}
					</div>
				</div>
				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						isLoading={eventsLoading}
						data={data.data}
						columns={columns}
						rowCount={data.count || 0}
						paginationMode="server"
						sortingMode="server"
						onPaginationModelChange={handlePaginationModelChange}
						onSortModelChange={handleSort}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort(sort || 'startTime:desc')]}
						pageSize={data.pageSize}
						page={data.page}
						onRowClick={handleRowClick}
						pagination
						getRowClassName={(p: { row: { markedForReview?: boolean } }) => (p.row.markedForReview ? 'error' : '')}
						onReload={revalidateEvents}
					/>
				</Paper>
			</div>

			{isFormOpened && (
				<EventForm
					isOpen={true}
					data={defaultEventData}
					onClose={handleCloseForm}
					onSubmit={handleAddEvent}
					types={types}
					tags={tags}
					singleGroupAsOwner={usesSubEvents}
					usesSubEvents={usesSubEvents}
					usesTags={usesTags}
				/>
			)}
		</>
	);
}
